/**
 * PortInfoPanel — context for the port the inspector is looking at.
 *
 * Opens from the high-zoom toolbar in the top-right of the map. Pulls
 * data from two free, well-known sources:
 *
 *   1. Mapbox Reverse Geocoding API — returns the human place name +
 *      country code at the current map center. Already gated by the
 *      same REACT_APP_MAPBOX_TOKEN that powers the basemap, so no
 *      new credentials.
 *
 *   2. Wikipedia REST API (en.wikipedia.org/api/rest_v1) — returns
 *      a clean summary, thumbnail, and canonical URL for the named
 *      place. Public API, no auth, generous rate-limit. The same
 *      pipeline already powers the vessel `wiki` field on the
 *      VesselProfileDrawer, so failure modes / fallbacks are
 *      battle-tested.
 *
 * The panel is intentionally information-light: name, country, LOCODE
 * if we can guess one, a 3-line description, and outbound links to
 * Marine Traffic / OpenStreetMap so an inspector new to the port can
 * dig deeper without us trying to embed everything.
 */
import React, { useEffect, useState } from 'react';
import {
  X,
  ExternalLink,
  MapPin,
  Globe,
  AlertCircle,
  Video,
} from 'lucide-react';
import LocodeBadge from '../shared/LocodeBadge';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

interface Props {
  lng: number;
  lat: number;
  onClose: () => void;
}

interface MapboxFeature {
  id: string;
  text?: string;
  place_name?: string;
  place_type?: string[];
  context?: Array<{ id: string; short_code?: string; text?: string }>;
}

interface WikiSummary {
  title?: string;
  description?: string;
  extract?: string;
  thumbnail?: { source?: string };
  content_urls?: { desktop?: { page?: string } };
}

/** Country ISO-2 → flag emoji (used for a tiny visual cue). */
function flagFor(iso2: string | undefined): string {
  if (!iso2 || iso2.length !== 2) return '';
  const upper = iso2.toUpperCase();
  // Manual char-by-char to avoid the ES5 target's "can only iterate
  // strings with downlevelIteration" warning.
  const a = 0x1F1A5 + upper.charCodeAt(0);
  const b = 0x1F1A5 + upper.charCodeAt(1);
  return String.fromCodePoint(a, b);
}

/**
 * Curated public CCTV / webcam directory for major ports.
 *
 * Keys are UN/LOCODE; values link to the port authority's own live
 * camera page (where one exists) and trusted aggregators. We don't
 * embed video streams directly — port authority pages frame their
 * streams with rate-limit / abuse protections that a hot-link would
 * bypass, and most use HLS or rtmp which need their own player.
 *
 * Inspector workflow: open the panel, click a webcam → opens the
 * official source in a new tab.
 *
 * Coverage today (the 12 highest-traffic ports + a few regional
 * inspector hubs). For any port outside this list the panel shows a
 * fallback Earthcam search by place name, which works for hundreds
 * more locations.
 */
interface PortCam {
  label: string;
  url: string;
  /** Source operator — shown as the muted sub-line. */
  operator: string;
}

const PORT_CAMS: Record<string, PortCam[]> = {
  // Europe — busiest ports
  NLRTM: [
    { label: 'Port of Rotterdam · live cams',  url: 'https://www.portofrotterdam.com/en/news-and-press-releases/webcams', operator: 'Port of Rotterdam Authority' },
    { label: 'Maasvlakte / Yangtze harbour',   url: 'https://www.havenrotterdam.live/',                                  operator: 'Independent live feed' },
  ],
  DEHAM: [
    { label: 'Port of Hamburg · HD live cams', url: 'https://www.hafen-hamburg.de/en/livecam/',                          operator: 'Hamburg Port Authority' },
  ],
  BEANR: [
    { label: 'Port of Antwerp-Bruges · cams',  url: 'https://www.portofantwerpbruges.com/en/about-port/webcams',        operator: 'Port of Antwerp-Bruges' },
  ],
  GBLON: [
    { label: 'Port of London · river cams',    url: 'https://www.pla.co.uk/Safety/CCTV-and-PLA-Cameras',                operator: 'Port of London Authority' },
  ],
  GBFXT: [
    { label: 'Port of Felixstowe webcam',      url: 'https://www.portoffelixstowe.co.uk/about/webcam.html',             operator: 'Hutchison Ports' },
  ],
  ESALG: [
    { label: 'Port of Algeciras · TraceCam',   url: 'https://www.apba.es/webcams',                                       operator: 'APBA' },
  ],
  ESBCN: [
    { label: 'Port de Barcelona · live',       url: 'https://www.portdebarcelona.cat/en/web/el-port/webcams',           operator: 'Port de Barcelona' },
  ],
  ITGOA: [
    { label: 'Port of Genoa · live',           url: 'https://www.portsofgenoa.com/en/webcams.html',                     operator: 'AdSP del Mar Ligure Occidentale' },
  ],
  // Asia
  SGSIN: [
    { label: 'PSA Singapore · port view',      url: 'https://www.maritimesg.com/portview',                              operator: 'MPA Singapore' },
  ],
  HKHKG: [
    { label: 'Hong Kong Marine Department',    url: 'https://www.mardep.gov.hk/en/aton/livecam.html',                   operator: 'HK Marine Department' },
  ],
  CNSHA: [
    { label: 'Shanghai port · marine traffic', url: 'https://www.marinetraffic.com/en/ais/home/centerx:121.5/centery:31.2/zoom:11', operator: 'MarineTraffic' },
  ],
  // North America
  USNYC: [
    { label: 'NY/NJ harbour cameras',          url: 'https://511ny.org/cctv/list',                                       operator: 'NYS DOT' },
    { label: 'Verrazzano Bridge live',         url: 'https://www.earthcam.com/usa/newyork/newyorkharbor/',              operator: 'EarthCam' },
  ],
  USLAX: [
    { label: 'Port of Los Angeles · live',     url: 'https://www.portoflosangeles.org/community/webcams',              operator: 'Port of LA' },
  ],
  USLGB: [
    { label: 'Port of Long Beach · live',      url: 'https://polb.com/port-info/news-and-press/webcams/',              operator: 'Port of Long Beach' },
  ],
  // Middle East
  AEDXB: [
    { label: 'Port Rashid · DP World view',    url: 'https://www.dpworld.com/en/uae/locations',                          operator: 'DP World' },
  ],
  // Australia
  AUMEL: [
    { label: 'Port of Melbourne · live',       url: 'https://www.portofmelbourne.com/about-us/our-port/webcams/',        operator: 'Port of Melbourne' },
  ],
  AUSYD: [
    { label: 'Port Authority of NSW · cams',   url: 'https://www.portauthoritynsw.com.au/sydney-harbour/sydney-harbour-camera/', operator: 'Port Authority NSW' },
  ],
};

export default function PortInfoPanel({ lng, lat, onClose }: Props) {
  const [geo, setGeo] = useState<MapboxFeature | null>(null);
  const [wiki, setWiki] = useState<WikiSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Esc closes; body scroll lock not needed since the panel is non-modal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Fetch + cache as the panel mounts. We don't memoise across the
  // session because the inspector is likely panning to inspect different
  // ports — fresh fetch per open is the right cost trade-off.
  useEffect(() => {
    let cancelled = false;
    async function go() {
      setLoading(true);
      setError(null);
      try {
        if (!MAPBOX_TOKEN) {
          throw new Error('Map access not configured');
        }
        // 1. Reverse geocode — biased toward "place" (city, port) +
        //    "locality". Mapbox returns the closest match within
        //    ~100km, ranked by relevance.
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&types=place,locality,district&limit=1`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Geocoding ${res.status}`);
        const json = (await res.json()) as { features?: MapboxFeature[] };
        const feature = json.features?.[0];
        if (cancelled) return;
        setGeo(feature ?? null);

        // 2. Wikipedia summary — keyed on the place name we just got.
        if (feature?.text) {
          try {
            const w = await fetch(
              `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(feature.text)}`,
            );
            if (w.ok) {
              const wj = (await w.json()) as WikiSummary;
              if (!cancelled) setWiki(wj);
            }
          } catch {
            // Wikipedia is best-effort — silent fall-through.
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load port info');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    go();
    return () => { cancelled = true; };
  }, [lng, lat]);

  const placeName = geo?.text ?? wiki?.title ?? 'Unknown location';
  const countryCtx = geo?.context?.find((c) => c.id?.startsWith('country'));
  const countryIso = countryCtx?.short_code?.toUpperCase();
  const countryName = countryCtx?.text;
  const fullPlace = geo?.place_name ?? placeName;
  const wikiUrl = wiki?.content_urls?.desktop?.page;

  // Try to derive a UN/LOCODE — we don't have a full lookup table on
  // the client (that's R-010A in the backlog), but we can fold in
  // country + first 3 letters of the place text as a *guess* that the
  // inspector can verify. Marked as "guess" so it isn't quoted as fact.
  const guessLocode = (() => {
    if (!countryIso || !geo?.text) return null;
    const tag = geo.text.toUpperCase().replace(/[^A-Z]/g, '');
    if (tag.length < 3) return null;
    return `${countryIso}${tag.slice(0, 3)}`;
  })();

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        aria-hidden="true"
        style={{ background: 'rgba(2,6,23,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div
        role="dialog"
        aria-label={`Information about ${placeName}`}
        className="fixed top-0 right-0 h-full w-[420px] max-w-[92vw] z-50 bg-slate-950/98 border-l border-cyan-500/30 shadow-2xl text-slate-100 overflow-y-auto"
        style={{ boxShadow: '0 0 40px -10px rgba(0,0,0,0.7)' }}
      >
        <header className="sticky top-0 px-5 py-3 bg-slate-950/95 backdrop-blur-md border-b border-slate-800 flex items-center gap-3">
          <MapPin className="w-4 h-4 text-cyan-300 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">
              Port / location info
            </div>
            <div className="text-base font-bold text-white truncate">
              {flagFor(countryIso)} {placeName}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-white shrink-0 p-1 -mr-1"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-4">
          {loading && (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-4 rounded bg-slate-800 animate-pulse" />
              ))}
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-2 text-xs">
              <AlertCircle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
              <div className="text-amber-200">{error}</div>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Identity row */}
              <section>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Identity</div>
                <Row label="Place" value={fullPlace} />
                {countryName && <Row label="Country" value={`${flagFor(countryIso)} ${countryName} ${countryIso ? `· ${countryIso}` : ''}`} />}
                {guessLocode && (
                  <Row
                    label="UN/LOCODE (guess)"
                    valueEl={
                      <span className="inline-flex items-center gap-1.5">
                        <LocodeBadge locode={guessLocode} />
                        <span className="text-[10px] text-slate-500">verify against UNECE list</span>
                      </span>
                    }
                  />
                )}
                <Row label="Coordinates" value={`${lat.toFixed(4)}°N, ${lng.toFixed(4)}°E`} mono />
              </section>

              {/* Description */}
              {wiki?.extract && (
                <section>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">About</div>
                  {wiki.thumbnail?.source && (
                    <img
                      src={wiki.thumbnail.source}
                      alt=""
                      className="w-full h-32 object-cover rounded-lg border border-slate-800 mb-2"
                      loading="lazy"
                    />
                  )}
                  <p className="text-[13px] text-slate-300 leading-relaxed">
                    {wiki.extract}
                  </p>
                </section>
              )}

              {/* Public webcams — port authority live streams + curated
                  fallbacks. When the guessed LOCODE matches one of the
                  major ports in PORT_CAMS we link to the official feed
                  and any reliable secondary aggregator. Otherwise we
                  drop in an EarthCam search keyed on the place name —
                  works for ~hundreds of ports we don't curate. */}
              {(() => {
                const cams = guessLocode ? PORT_CAMS[guessLocode] : null;
                const fallbackEarthCam = `https://www.earthcam.com/search/ft_search.php?term=${encodeURIComponent(`${placeName} port`)}`;
                return (
                  <section>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5 inline-flex items-center gap-1.5">
                      <Video className="w-3 h-3 text-cyan-300" />
                      Public webcams
                    </div>
                    {cams && cams.length > 0 ? (
                      <div className="grid grid-cols-1 gap-1.5">
                        {cams.map((c) => (
                          <ExternalLinkRow
                            key={c.url}
                            href={c.url}
                            label={c.label}
                            sub={c.operator}
                            iconHint="cam"
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <ExternalLinkRow
                          href={fallbackEarthCam}
                          label={`Search EarthCam for "${placeName}"`}
                          sub="Aggregator with hundreds of port + harbour cams"
                          iconHint="cam"
                        />
                        <ExternalLinkRow
                          href={`https://www.google.com/search?q=${encodeURIComponent(`${placeName} port live webcam`)}`}
                          label="Google · port live webcam"
                          sub="Quick search across port authority pages"
                          iconHint="cam"
                        />
                      </div>
                    )}
                    <p className="text-[10px] text-slate-500 leading-relaxed mt-2">
                      Cameras open in a new tab on the operator's own
                      page. Streams use the operator's own player and
                      are subject to their access terms.
                    </p>
                  </section>
                );
              })()}

              {/* Outbound links — when the inspector wants to dig deeper.
                  Marine Traffic for live AIS view, OpenStreetMap for an
                  authoritative map of port infrastructure, Wikipedia
                  for the operator/regulator background. */}
              <section>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Useful links</div>
                <div className="grid grid-cols-1 gap-1.5">
                  <ExternalLinkRow
                    href={`https://www.marinetraffic.com/en/ais/home/centerx:${lng}/centery:${lat}/zoom:13`}
                    label="MarineTraffic"
                    sub="Live AIS view of this area"
                  />
                  <ExternalLinkRow
                    href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}&zoom=15`}
                    label="OpenStreetMap"
                    sub="Authoritative map of port infrastructure"
                  />
                  {wikiUrl && (
                    <ExternalLinkRow
                      href={wikiUrl}
                      label="Wikipedia"
                      sub="Background on the city / port authority"
                    />
                  )}
                  <ExternalLinkRow
                    href={`https://www.windy.com/?${lat.toFixed(3)},${lng.toFixed(3)},10`}
                    label="Windy.com"
                    sub="Marine weather + tides"
                  />
                </div>
              </section>

              {/* Footer note about data confidence */}
              <div className="text-[10px] text-slate-500 leading-relaxed pt-2 border-t border-slate-800">
                Place info is the closest match within ~100 km. Coordinates are the current map center.
                Cross-check the UN/LOCODE before quoting it on a regulator-facing form.
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Row({
  label,
  value,
  valueEl,
  mono,
}: {
  label: string;
  value?: string;
  valueEl?: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-[12px] border-b border-slate-800/60 last:border-b-0">
      <span className="text-slate-400 shrink-0">{label}</span>
      {valueEl ?? (
        <span className={`text-slate-100 text-right ${mono ? 'font-mono text-[11px]' : ''}`}>
          {value ?? '—'}
        </span>
      )}
    </div>
  );
}

function ExternalLinkRow({
  href,
  label,
  sub,
  iconHint,
}: {
  href: string;
  label: string;
  sub: string;
  /** "cam" → video icon; default → globe icon. */
  iconHint?: 'cam';
}) {
  const Icon = iconHint === 'cam' ? Video : Globe;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 hover:border-cyan-500/40 hover:bg-slate-900 transition-colors"
    >
      <Icon className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-semibold text-white">{label}</div>
        <div className="text-[10px] text-slate-400 truncate">{sub}</div>
      </div>
      <ExternalLink className="w-3 h-3 text-slate-500 shrink-0" />
    </a>
  );
}
